import React, { useState } from 'react';
import { 
  Box,
  Paper,
  alpha,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse
} from '@mui/material';
import { 
  People as PeopleIcon,
  Dns as DnsIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  ListAlt as LogIcon,
  SummarizeOutlined as SummarizeOutlinedIcon,
  ContactMail as ContactMailIcon,
  ExpandLess,
  ExpandMore,
  Visibility as VisibilityIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  PolylineOutlined as PolylineOutlinedIcon,
  IntegrationInstructionsOutlined as IntegrationInstructionsOutlinedIcon,
  DnsOutlined as DnsOutlinedIcon,
  // Reporting Management Icons
  CalendarTodayOutlined as CalendarIcon,
  Computer as WorkstationsIcon,
  MedicalServicesOutlined as ExaminationsIcon,
  Assignment as ScanProtocolsIcon,
  AccountTreeOutlined as StructuredIcon,
  TextFieldsOutlined as FreetextIcon,
  TextSnippetOutlined as SnippetsIcon,
  DescriptionOutlined as LayoutIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import UserManagement from './admin/UserManagement';
import AddressManagement from './admin/AddressManagement';
import ServerManagement from './admin/ServerManagement';
import PasswordManagement from './admin/PasswordManagement';
import LogViewer from './admin/LogViewer';
import TenantManagement from './admin/TenantManagement';
import ReportingManagement from './admin/ReportingManagement';

const ADMIN_PAGE_CONTENT_ZOOM = 0.8;

const Admin = ({ isAdmin, canManageReporting }) => {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState('tenantManagement');
  const [serverManagementOpen, setServerManagementOpen] = useState(false);
  const [serverManagementTab, setServerManagementTab] = useState(0);
  const [reportingManagementOpen, setReportingManagementOpen] = useState(false);
  const [reportingManagementTab, setReportingManagementTab] = useState(0);

  const handleItemClick = (key) => {
    setActiveItem(key);
    // Serververwaltung und Berichteinstellungen einklappen wenn auf andere Hauptseite geklickt wird
    setServerManagementOpen(false);
    setReportingManagementOpen(false);
  };

  const handleServerManagementToggle = () => {
    setServerManagementOpen(!serverManagementOpen);
    // Wenn aufgeklappt wird und noch kein Server-Item aktiv ist, setze auf erstes
    if (!serverManagementOpen) {
      setActiveItem('server_viewer');
      setServerManagementTab(0);
      setReportingManagementOpen(false); // Reporting schließen
    }
  };

  const handleServerSubItemClick = (key, tabIndex) => {
    setActiveItem(key);
    setServerManagementTab(tabIndex);
  };

  const handleReportingManagementToggle = () => {
    setReportingManagementOpen(!reportingManagementOpen);
    // Wenn aufgeklappt wird und noch kein Reporting-Item aktiv ist, setze auf erstes
    if (!reportingManagementOpen) {
      setActiveItem('reporting_calendar');
      setReportingManagementTab(0);
      setServerManagementOpen(false); // Server schließen
    }
  };

  const handleReportingSubItemClick = (key, tabIndex) => {
    setActiveItem(key);
    setReportingManagementTab(tabIndex);
  };

  // Hauptnavigationspunkte
  // KEYCLOAK_MIGRATION: userManagement + passwordManagement ausgeblendet (Keycloak Admin Console)
  const mainNavItems = isAdmin ? [
    // { key: 'userManagement', label: t('admin.tabs.userManagement'), icon: <PeopleIcon /> },
    // { key: 'passwordManagement', label: t('admin.tabs.passwordManagement'), icon: <SecurityIcon /> },
    { key: 'tenantManagement', label: t('admin.tabs.tenantManagement'), icon: <BusinessIcon /> },
    { key: 'serverManagement', label: t('admin.tabs.serverManagement'), icon: <DnsIcon />, hasChildren: true, childType: 'server' },
    { key: 'reportingManagement', label: t('admin.tabs.reportingManagement'), icon: <SummarizeOutlinedIcon />, hasChildren: true, childType: 'reporting' },
    { key: 'addressManagement', label: t('admin.tabs.addressManagement'), icon: <ContactMailIcon /> },
    { key: 'logViewer', label: t('admin.tabs.logViewer'), icon: <LogIcon /> }
  ] : [
    { key: 'reportingManagement', label: t('admin.tabs.reportingManagement'), icon: <SummarizeOutlinedIcon />, hasChildren: true, childType: 'reporting' },
    { key: 'addressManagement', label: t('admin.tabs.addressManagement'), icon: <ContactMailIcon /> }
  ];

  // ServerManagement Untermenü
  const serverManagementItems = [
    { key: 'server_viewer', label: t('admin.serverManagement.viewerSettings'), icon: <VisibilityIcon />, tabIndex: 0 },
    { key: 'server_pacs', label: t('admin.serverManagement.pacsSettings'), icon: <Inventory2OutlinedIcon />, tabIndex: 1 },
    { key: 'server_dicom', label: t('admin.serverManagement.dicomSettings'), icon: <PolylineOutlinedIcon />, tabIndex: 2 },
    { key: 'server_hl7', label: t('admin.serverManagement.hl7Settings'), icon: <IntegrationInstructionsOutlinedIcon />, tabIndex: 3 },
    { key: 'server_config', label: t('admin.serverManagement.adminConfig'), icon: <DnsOutlinedIcon />, tabIndex: 4 }
  ];

  // ReportingManagement Untermenü
  const reportingManagementItems = [
    { key: 'reporting_calendar', label: t('admin.reportingManagement.calendarSettings.title'), icon: <CalendarIcon />, tabIndex: 0 },
    { key: 'reporting_workstations', label: t('admin.reportingManagement.workstations.title'), icon: <WorkstationsIcon />, tabIndex: 1 },
    { key: 'reporting_examinations', label: t('admin.reportingManagement.examinations.title'), icon: <ExaminationsIcon />, tabIndex: 2 },
    { key: 'reporting_scanprotocols', label: t('admin.reportingManagement.scanProtocols.title'), icon: <ScanProtocolsIcon />, tabIndex: 3 },
    { key: 'reporting_structured', label: t('admin.reportingManagement.structuredReporting.title'), icon: <StructuredIcon />, tabIndex: 4 },
    { key: 'reporting_freetext', label: t('admin.reportingManagement.freetextReporting.title'), icon: <FreetextIcon />, tabIndex: 5 },
    { key: 'reporting_snippets', label: t('admin.reportingManagement.snippets.title'), icon: <SnippetsIcon />, tabIndex: 6 },
    { key: 'reporting_layout', label: t('admin.reportingManagement.reportLayout.title'), icon: <LayoutIcon />, tabIndex: 7 }
  ];

  // Prüfen ob ein ServerManagement-Unteritem aktiv ist
  const isServerItemActive = serverManagementItems.some(item => item.key === activeItem);
  
  // Prüfen ob ein ReportingManagement-Unteritem aktiv ist
  const isReportingItemActive = reportingManagementItems.some(item => item.key === activeItem);

  // Content rendern basierend auf aktivem Item
  const renderContent = () => {
    // Server Management Unterseiten
    if (activeItem.startsWith('server_')) {
      return <ServerManagement initialTab={serverManagementTab} hideHeader={true} />;
    }
    
    // Reporting Management Unterseiten
    if (activeItem.startsWith('reporting_')) {
      return <ReportingManagement initialTab={reportingManagementTab} hideHeader={true} />;
    }
    
    switch (activeItem) {
      // KEYCLOAK_MIGRATION: userManagement + passwordManagement ausgeblendet
      // case 'userManagement':
      //   return <UserManagement />;
      // case 'passwordManagement':
      //   return <PasswordManagement />;
      case 'tenantManagement':
        return <TenantManagement />;
      case 'addressManagement':
        return <AddressManagement />;
      case 'logViewer':
        return <LogViewer />;
      default:
        return <TenantManagement />;
    }
  };

  // Gemeinsame Styles für Nav Items
  const navItemStyles = {
    minHeight: 48,
    px: 2.5,
    py: 1,
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: 'action.hover',
    },
  };

  const activeNavItemStyles = {
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
    color: 'primary.main',
    fontWeight: 600,
    borderLeft: 4,
    borderColor: 'primary.main',
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      px: { xs: 2, sm: 3 },
      boxSizing: 'border-box'
    }}>
      <Box sx={{
        zoom: ADMIN_PAGE_CONTENT_ZOOM,
        width: '100%'
      }}>
      {/* Main Content with Sidebar */}
      <Paper 
        sx={{ 
          display: 'flex',
          height: `calc((100vh - 150px) / ${ADMIN_PAGE_CONTENT_ZOOM})`,
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        {/* Sidebar Navigation */}
        <Box 
          sx={{ 
            width: 260,
            minWidth: 260,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),
            overflow: 'auto'
          }}
        >
          <List component="nav" disablePadding>
            {mainNavItems.map((item) => (
              <React.Fragment key={item.key}>
                {item.hasChildren && item.childType === 'server' ? (
                  // ServerManagement mit Untermenü
                  <>
                    <ListItemButton
                      onClick={handleServerManagementToggle}
                      sx={{
                        ...navItemStyles,
                        ...(isServerItemActive ? activeNavItemStyles : {}),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: isServerItemActive ? 'primary.main' : 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label} 
                        primaryTypographyProps={{ 
                          fontWeight: isServerItemActive ? 600 : 500,
                          fontSize: '0.95rem'
                        }}
                      />
                      {serverManagementOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={serverManagementOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {serverManagementItems.map((subItem) => (
                          <ListItemButton
                            key={subItem.key}
                            onClick={() => handleServerSubItemClick(subItem.key, subItem.tabIndex)}
                            sx={{
                              ...navItemStyles,
                              pl: 4,
                              ...(activeItem === subItem.key ? activeNavItemStyles : {}),
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: activeItem === subItem.key ? 'primary.main' : 'text.secondary' }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={subItem.label}
                              primaryTypographyProps={{ 
                                fontWeight: activeItem === subItem.key ? 600 : 400,
                                fontSize: '0.875rem'
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : item.hasChildren && item.childType === 'reporting' ? (
                  // ReportingManagement mit Untermenü
                  <>
                    <ListItemButton
                      onClick={handleReportingManagementToggle}
                      sx={{
                        ...navItemStyles,
                        ...(isReportingItemActive ? activeNavItemStyles : {}),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: isReportingItemActive ? 'primary.main' : 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label} 
                        primaryTypographyProps={{ 
                          fontWeight: isReportingItemActive ? 600 : 500,
                          fontSize: '0.95rem'
                        }}
                      />
                      {reportingManagementOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={reportingManagementOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {reportingManagementItems.map((subItem) => (
                          <ListItemButton
                            key={subItem.key}
                            onClick={() => handleReportingSubItemClick(subItem.key, subItem.tabIndex)}
                            sx={{
                              ...navItemStyles,
                              pl: 4,
                              ...(activeItem === subItem.key ? activeNavItemStyles : {}),
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: activeItem === subItem.key ? 'primary.main' : 'text.secondary' }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={subItem.label}
                              primaryTypographyProps={{ 
                                fontWeight: activeItem === subItem.key ? 600 : 400,
                                fontSize: '0.875rem'
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  // Normale Navigation Items
                  <ListItemButton
                    onClick={() => handleItemClick(item.key)}
                    sx={{
                      ...navItemStyles,
                      ...(activeItem === item.key ? activeNavItemStyles : {}),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: activeItem === item.key ? 'primary.main' : 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{ 
                        fontWeight: activeItem === item.key ? 600 : 500,
                        fontSize: '0.95rem'
                      }}
                    />
                  </ListItemButton>
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {renderContent()}
        </Box>
      </Paper>
      </Box>
    </Box>
  );
};

export default Admin;
